import { z } from 'zod';
const MAX_TRIGGER_DEPTH = 20;
const triggerExprBase = z.lazy(() => z.union([
    z.object({ type: z.literal('always') }),
    z.object({ type: z.literal('topic'), key: z.string().min(1, 'Выберите тему') }),
    z.object({ type: z.literal('all'), of: z.array(triggerExprBase).min(1, 'Добавьте условие') }),
    z.object({ type: z.literal('any'), of: z.array(triggerExprBase).min(1, 'Добавьте условие') }),
    z.object({ type: z.literal('not'), of: triggerExprBase }),
]));
const MAX_TRIGGER_NODES = 1000; // defense-in-depth against wide-but-shallow abuse
/**
 * Iterative bounded pre-check — NEVER recurses on the JS call stack. Walks the
 * raw blob with an explicit stack, capping depth (≤MAX_TRIGGER_DEPTH) and total
 * node count (≤MAX_TRIGGER_NODES) BEFORE the recursive `triggerExprBase` parse,
 * so a pathological deep/wide tree fails with a clean ZodError instead of a
 * RangeError. Only descends the structural `of` key; full shape validation is
 * left to `triggerExprBase`.
 */
const triggerStructureGuard = (raw, ctx) => {
    const stack = [{ node: raw, depth: 1 }];
    let nodes = 0;
    while (stack.length > 0) {
        const { node, depth } = stack.pop();
        if (node === null || typeof node !== 'object')
            continue;
        nodes += 1;
        if (nodes > MAX_TRIGGER_NODES) {
            ctx.addIssue({
                code: 'custom',
                message: `Условие слишком большое (>${MAX_TRIGGER_NODES} узлов)`,
            });
            return;
        }
        if (depth > MAX_TRIGGER_DEPTH) {
            ctx.addIssue({ code: 'custom', message: `Условие слишком глубокое (>${MAX_TRIGGER_DEPTH})` });
            return;
        }
        const of = node.of;
        if (Array.isArray(of)) {
            for (const child of of)
                stack.push({ node: child, depth: depth + 1 });
        }
        else if (of !== undefined) {
            stack.push({ node: of, depth: depth + 1 });
        }
    }
};
export const TriggerExprSchema = z
    .unknown()
    .superRefine(triggerStructureGuard)
    .pipe(triggerExprBase);
/**
 * Parse + validate an unknown blob (e.g. `pipeline.trigger` jsonb) into a typed
 * `TriggerExpr`. Throws ZodError on malformed/over-deep input.
 */
export const parseTrigger = (raw) => TriggerExprSchema.parse(raw);
/** Collect every `topic` leaf key referenced anywhere in a trigger expression (may contain duplicates). */
export const collectTopicKeys = (expr) => {
    switch (expr.type) {
        case 'always':
            return [];
        case 'topic':
            return [expr.key];
        case 'not':
            return collectTopicKeys(expr.of);
        case 'all':
        case 'any':
            return expr.of.flatMap(collectTopicKeys);
    }
};
/** Evaluate a trigger against the set of classified label keys. */
export const evaluateTrigger = (expr, labels) => {
    switch (expr.type) {
        case 'always':
            return true;
        case 'topic':
            return labels.has(expr.key);
        case 'all':
            return expr.of.every((e) => evaluateTrigger(e, labels));
        case 'any':
            return expr.of.some((e) => evaluateTrigger(e, labels));
        case 'not':
            return !evaluateTrigger(expr.of, labels);
    }
};
//# sourceMappingURL=trigger.js.map